// @ts-strict-ignore
import React, { useState, useCallback, useRef, useLayoutEffect, useMemo } from 'react'
import { View, Animated } from 'react-native'

// Components
import IntakeReplyForm from '../IntakeReplyForm'
import IntakeAnswer from '../IntakeAnswer'

// Helpers
import { isWeb } from 'APP/Helpers/checkPlatform'
import { RichContentTypeEnum } from 'APP/Services/Mattermost/types'

// Styles
import styled, { css } from 'styled-components/native'
import { ResponsiveView } from 'APP/Converse/Layout'

interface ReplyPanelViewProps {
  isPromptMultiSubprompt: boolean
  noGrow: boolean
  shrink: boolean
}

const ReplyPanelView = styled(ResponsiveView)<ReplyPanelViewProps>`
  flex-grow: ${({ noGrow }) => (noGrow ? 0 : 1)};
  flex-shrink: ${({ shrink }) => (shrink ? 1 : 0)};
  position: relative;
  display: flex;
  width: 100%;
  justify-content: ${({ isPromptMultiSubprompt }) =>
    isPromptMultiSubprompt ? 'flex-start' : 'flex-end'};
  align-items: ${({ isPromptMultiSubprompt }) => (isPromptMultiSubprompt ? 'stretch' : 'flex-end')};
  z-index: 10;
  overflow: ${({ isPromptMultiSubprompt }) => (isPromptMultiSubprompt ? 'hidden' : 'visible')};
`

const ReplyPanelAnswerContainer = styled(Animated.View)`
  ${() => {
    switch (isWeb()) {
      case true:
        return css`
          position: absolute;
          top: 0px;
          right: 0px;
        `
      default:
        return css``
    }
  }}
`

const ReplyPanelContainer = styled(Animated.View)<{ expand: boolean }>`
  flex-grow: ${({ expand }) => (expand ? 1 : 0)};
  align-self: ${({ expand }) => (expand ? 'stretch' : 'auto')};
`

interface ReplyFormContainerProps {
  isPromptMultiSubprompt: boolean
  expand: boolean
  isFullList: boolean
}

const ReplyFormContainer = styled(View)<ReplyFormContainerProps>`
  flex-grow: ${({ isPromptMultiSubprompt, expand, isFullList }) =>
    isPromptMultiSubprompt || expand || isFullList ? 1 : 0};
  align-self: ${({ expand }) => (expand ? 'stretch' : 'auto')};
`

interface IntakeReplyPanelProps {
  shrink?: boolean
  noGrow?: boolean
  overlayReplyForm?: boolean
  expandReplyForm?: boolean
  showReplyPanel: boolean
  post: any // @todo: ANY - be more specific here
  answers?: any[] // @todo: ANY - be more specific here
  retryPost: (postId: string) => void
  onAnswerSend: (...args: any[]) => void // @todo: ANY - be more specific here
  resolveAttachmentUrl: (url: string) => string
  resolveGetPostFileMetadata: (fileId: string) => Record<string, any> // @todo: ANY - be more specific here
}

const IntakeReplyPanel: React.FC<IntakeReplyPanelProps> = ({
  shrink,
  noGrow,
  overlayReplyForm,
  expandReplyForm,
  showReplyPanel,
  post,
  answers: answersProp,
  retryPost,
  onAnswerSend,
  resolveAttachmentUrl,
  resolveGetPostFileMetadata,
}) => {
  const [answers, setAnswers] = useState<any[]>([]) // @todo: ANY - be more specific here
  const [replyFormDisabled, setReplyFormDisabled] = useState(false)

  // These properties can affect layout, and shouldn't be changed during animations.
  // These props are also used to inform when we should transition. So we use a buffer
  // for where they impact render (besides animations) so things don't snap in/out.
  const [layoutProps, setLayoutProps] = useState({
    overlayReplyForm,
    expandReplyForm,
    noGrow,
    shrink,
    post,
  })

  const isPromptMultiSubprompt = layoutProps?.post?.props?.type === 'prompt_multi_subprompt'

  // Bad practice to use 'props' as a variable name inside a react component.
  // This is actually postProps.
  // TODO: Refactor
  const props = useMemo(() => {
    const propsValue = layoutProps?.post?.props || {}
    if (isPromptMultiSubprompt) {
      // Bad practice to mutate the post structure on its way to lower components
      // TODO: Refactor
      return {
        ...propsValue,
        title: layoutProps?.post?.message,
      }
    }
    return propsValue
  }, [layoutProps?.post, isPromptMultiSubprompt])

  const replyFormViewRef = useRef<View>(null)

  // When we have a singlechoice post with only one choice, Skip button layout or horizontal display variant
  // we want to make the reply form full width
  const isPromptText = props?.type === 'prompt_text'
  const isSingleChoiceWithOneOption =
    props?.type === 'prompt_choice_single' && props?.choices?.length === 1
  const isSkipButtonLayout =
    props?.type === 'prompt_choice_single' &&
    props?.member_app_fullscreen?.layout_style === 'skip_button'
  const isHorizontalDisplayVariant = props?.choices_display_variant === 'horizontal'
  const isProviderListChoice = props?.type === 'prompt_provider_list'
  const isFullList = props?.choices_display_variant === 'full_list'
  const hasLoadingSteps = layoutProps?.post?.props?.structured_description?.some(
    (item: { type: string }) => item.type === RichContentTypeEnum.Steps
  )
  const hasFinishedLoading = hasLoadingSteps ? showReplyPanel : true
  const containerWidth =
    isPromptText ||
    isSingleChoiceWithOneOption ||
    isSkipButtonLayout ||
    isHorizontalDisplayVariant ||
    isProviderListChoice ||
    isFullList
      ? '100%'
      : 'auto'

  const baseContainerStyles = {
    width: containerWidth,
    ...(isPromptMultiSubprompt && { flexGrow: 1 }),
    ...(isFullList && { flexGrow: 1 }),
  }

  const replyForm = {
    width: '100%',
    maxWidth: '800px',
    alignSelf: 'center',
  }

  const displayedAnswers = useMemo(
    () => (answersProp ?? []).filter((answer) => answer?.props?.type !== 'prompt_file_response'),
    [answersProp]
  )

  useLayoutEffect(() => {
    if (layoutProps.post?.id !== post?.id) {
      setLayoutProps({
        overlayReplyForm,
        expandReplyForm,
        noGrow,
        shrink,
        post,
      })
    }
  }, [
    post,
    answers,
    showReplyPanel,
    layoutProps.post?.id,
    overlayReplyForm,
    expandReplyForm,
    noGrow,
    shrink,
  ])

  const answerContainerAnimationState = useRef(new Animated.Value(0)).current

  let answerContainerAnimatedStyles = {}

  useLayoutEffect(() => {
    if (showReplyPanel && displayedAnswers.length > 0) {
      if (props?.member_app_fullscreen?.animate_answer) {
        setAnswers(displayedAnswers)
      }
    } else if (!showReplyPanel || answers.length > 0) {
      answerContainerAnimationState.setValue(0)
      setAnswers([])
    }
  }, [
    showReplyPanel,
    displayedAnswers,
    answers.length,
    props?.member_app_fullscreen?.animate_answer,
    answerContainerAnimationState,
  ])

  const sendAnswer = useCallback(
    (...args: any) => {
      // @todo: ANY - be more specific here
      setReplyFormDisabled(true)

      onAnswerSend(...args)
    },
    [onAnswerSend]
  )

  const ReplyForm = (
    <IntakeReplyForm
      key={layoutProps.post?.id || 'default-reply-panel-key'}
      {...props}
      style={[layoutProps.overlayReplyForm && baseContainerStyles, replyForm]}
      disabled={replyFormDisabled || !hasFinishedLoading}
      sendAnswer={sendAnswer}
      testID="INTAKE_REPLY_FORM"
    />
  )

  const shouldRenderReplyPanelContainer = !layoutProps.overlayReplyForm && answers.length === 0

  return (
    <>
      <ReplyPanelView
        isPromptMultiSubprompt={isPromptMultiSubprompt}
        shrink={layoutProps.shrink}
        noGrow={layoutProps.noGrow}
        testID="REPLY_PANEL_VIEW_CONTAINER"
      >
        <ReplyPanelAnswerContainer
          testID="INTAKE_ANSWER_CONTAINER"
          style={[answerContainerAnimatedStyles]}
        >
          {answers &&
            answers.map((answer, i) => (
              <IntakeAnswer
                key={`answer-${i}`}
                answer={answer}
                onRetry={retryPost}
                resolveAttachmentUrl={resolveAttachmentUrl}
                resolveGetPostFileMetadata={resolveGetPostFileMetadata}
                testID={`INTAKE_ANSWER_${i}`}
              />
            ))}
        </ReplyPanelAnswerContainer>
        {shouldRenderReplyPanelContainer && (
          <ReplyPanelContainer
            expand={layoutProps.expandReplyForm ?? false}
            style={[baseContainerStyles]}
          >
            <ReplyFormContainer
              isPromptMultiSubprompt={isPromptMultiSubprompt}
              expand={layoutProps.expandReplyForm ?? false}
              isFullList={isFullList}
              ref={replyFormViewRef}
            >
              {ReplyForm}
            </ReplyFormContainer>
          </ReplyPanelContainer>
        )}
      </ReplyPanelView>
      {layoutProps.overlayReplyForm && ReplyForm}
    </>
  )
}

export default IntakeReplyPanel
